import {
  type Extension,
  type Range,
  RangeSet,
  StateField,
} from "@codemirror/state";
import {
  Decoration,
  type DecorationSet,
  EditorView,
  GutterMarker,
  gutterLineClass,
} from "@codemirror/view";

export type DiffRange = {
  kind: "added" | "removed" | "added-word" | "removed-word";
  from: number;
  to: number;
};

const addedLine = Decoration.line({ class: "cm-diff-added" });
const removedLine = Decoration.line({ class: "cm-diff-removed" });
const addedWord = Decoration.mark({ class: "cm-diff-added-word" });
const removedWord = Decoration.mark({ class: "cm-diff-removed-word" });

class DiffGutterMarker extends GutterMarker {
  constructor(override readonly elementClass: string) {
    super();
  }
}
const addedGutter = new DiffGutterMarker("cm-diff-added");
const removedGutter = new DiffGutterMarker("cm-diff-removed");

export function diffExtension(ranges: DiffRange[]): Extension {
  if (ranges.length === 0) return [];

  const decorationsField = StateField.define<DecorationSet>({
    create(state) {
      const values: Range<Decoration>[] = [];
      for (const r of ranges) {
        const clampedTo = Math.min(r.to, state.doc.length);
        if (r.from >= clampedTo) continue;
        if (r.kind === "added" || r.kind === "removed") {
          const startLine = state.doc.lineAt(r.from).number;
          const endLine = state.doc.lineAt(
            Math.max(r.from, clampedTo - 1)
          ).number;
          const deco = r.kind === "added" ? addedLine : removedLine;
          for (let l = startLine; l <= endLine; l++) {
            values.push(deco.range(state.doc.line(l).from));
          }
        } else {
          const deco = r.kind === "added-word" ? addedWord : removedWord;
          values.push(deco.range(r.from, clampedTo));
        }
      }
      return Decoration.set(values, true);
    },
    update(value) {
      return value;
    },
    provide: (f) => EditorView.decorations.from(f),
  });

  const gutterField = StateField.define<RangeSet<GutterMarker>>({
    create(state) {
      const values: Range<GutterMarker>[] = [];
      for (const r of ranges) {
        if (r.kind !== "added" && r.kind !== "removed") continue;
        const clampedTo = Math.min(r.to, state.doc.length);
        if (r.from >= clampedTo) continue;
        const startLine = state.doc.lineAt(r.from).number;
        const endLine = state.doc.lineAt(
          Math.max(r.from, clampedTo - 1)
        ).number;
        const marker = r.kind === "added" ? addedGutter : removedGutter;
        for (let l = startLine; l <= endLine; l++) {
          values.push(marker.range(state.doc.line(l).from));
        }
      }
      return RangeSet.of(values, true);
    },
    update(value) {
      return value;
    },
    provide: (f) => gutterLineClass.from(f),
  });

  return [decorationsField, gutterField];
}
